import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useMemo, useEffect } from 'react';
import { CountTopFilter } from '../../common/CountTopFilter';
import { countsDisplays } from '../../../store/reducers/brandOrders';
import DateRange from '../../common/DateRange/DateRange';
import Panel from '../../common/Panel';
import Wall from '../../common/Wall';
import ActionButton from '../../common/ActionButton';
import { AnalyticsFilter } from '../Analytics/AnalyticsFilter';
import {
  dropshipOrdersReport,
  getDropshipOrders,
  setDropshipOrdersLoading,
  setDropshipOrdersOrderTypes,
  setDropshipOrdersSupplierIds,
} from '../../../store/actions/dropshipOrders';
import { setDropshipOrdersLastDayFilter } from '../../../store/actions/pageFilters';
import { SalesChannelIcon } from '../../icons/analytics';
import { supplierRetailerUtils } from '../../../utils/supplierRetailer';
import { dropshipProductsUtils } from '../../../utils/dropship-products';

export function RetailerDropshipOrdersPanel() {
  const dispatch = useDispatch();
  const {
    retailerSuppliers,
    retailer: { supplier_retailers: supplierRetailersFromRetailer },
  } = useSelector((state) => state.role);

  const {
    count,
    pendingCount,
    completedCount,
    canceledCount,
    activeCount,
    orderTypeCounts,
    selectedSupplierIds = [],
    selectedOrderTypes = [],
  } = useSelector((state) => state.dropshipOrders);

  const orderTypeOptions = useMemo(() => dropshipProductsUtils.getOrderTypeOptions(orderTypeCounts), [orderTypeCounts]);

  const shouldShowOrderTypeFilter = dropshipProductsUtils.shouldShowOrderTypeOption(orderTypeCounts);

  const getOrders = (title) => dispatch(getDropshipOrders(title));

  const relevantSuppliers = useMemo(
    () => (retailerSuppliers?.length ? retailerSuppliers : supplierRetailersFromRetailer),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const salesChannelsOptions = useMemo(
    () => supplierRetailerUtils.getSalesChannelOptionsFromSuppliers(relevantSuppliers),
    [relevantSuppliers],
  );

  useEffect(() => {
    function setInitialSelectedSupplierIds() {
      if (relevantSuppliers?.length > 0 && selectedSupplierIds.length === 0) {
        const activeSupplierIds = relevantSuppliers.map((supplier) => supplier.supplier.id);

        dispatch(setDropshipOrdersSupplierIds(activeSupplierIds));
      }
    }

    function setInitialSelectedOrderTypes() {
      if (selectedOrderTypes.length === 0) {
        dispatch(setDropshipOrdersOrderTypes(orderTypeOptions.map((orderType) => orderType.value)));
      }
    }

    setInitialSelectedSupplierIds();
    setInitialSelectedOrderTypes();
  }, [dispatch, relevantSuppliers, selectedSupplierIds.length, selectedOrderTypes.length, orderTypeOptions]);

  const handleChangeRange = (startDate, endDate) => {
    dispatch(setDropshipOrdersLoading(true));
    dispatch(setDropshipOrdersLastDayFilter(startDate, endDate));
  };

  const counts = {
    all: count,
    processing: pendingCount,
    completed: completedCount,
    canceled: canceledCount,
  };

  return (
    <Panel fix>
      <Wall />

      <DateRange onChange={(startDate, endDate) => handleChangeRange(startDate, endDate)} />
      <Wall />

      {Object.entries(countsDisplays).map(([count, display]) => (
        <CountTopFilter
          key={count}
          name={count}
          onClick={getOrders}
          display={display}
          count={counts[count]}
          isActive={count === activeCount}
        />
      ))}
      <Wall />

      {relevantSuppliers?.length > 0 && (
        <>
          <AnalyticsFilter
            title="Sellers"
            options={salesChannelsOptions}
            onChange={(values) => dispatch(setDropshipOrdersSupplierIds(values))}
            selected={selectedSupplierIds}
            withSearch={relevantSuppliers?.length > 2}
            icon={<SalesChannelIcon />}
          />
          <Wall />
        </>
      )}

      {shouldShowOrderTypeFilter && (
        <>
          <AnalyticsFilter
            title="Type"
            options={orderTypeOptions}
            selected={selectedOrderTypes}
            onChange={(values) => dispatch(setDropshipOrdersOrderTypes(values))}
            icon={<FontAwesomeIcon icon={faBox} />}
          />
          <Wall />
        </>
      )}

      <ActionButton
        type="download"
        size={25}
        onClick={() => dispatch(dropshipOrdersReport())}
        tooltipContent="Export orders report"
      />
      <Wall />
    </Panel>
  );
}
